import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from 'designSystem/lib/utils'
import { CheckIcon } from 'lucide-react'
import { Checkbox as CheckboxPrimitive } from 'radix-ui'
import * as React from 'react'

const checkboxVariants = cva(
  'peer size-4 shrink-0 rounded-[4px] border border-input shadow-xs transition-shadow outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:bg-input/30 dark:aria-invalid:ring-destructive/40',
  {
    variants: {
      variant: {
        default:
          'data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground dark:data-[state=checked]:bg-primary',
        success:
          'data-[state=checked]:border-green-500 data-[state=checked]:bg-green-500 data-[state=checked]:text-green-950 dark:data-[state=checked]:bg-green-500',
      },
      interaction: {
        default: '',
        /**
         * Display-only checkbox inside a larger interactive row (e.g. a
         * CommandItem): the row owns the click, the box just reflects it.
         */
        presentation: 'pointer-events-none',
      },
    },
    defaultVariants: {
      variant: 'default',
      interaction: 'default',
    },
  }
)

const Checkbox = React.forwardRef<
  React.ComponentRef<typeof CheckboxPrimitive.Root>,
  React.ComponentProps<typeof CheckboxPrimitive.Root> &
    VariantProps<typeof checkboxVariants>
>(({ className, interaction, variant, ...props }, ref) => {
  return (
    <CheckboxPrimitive.Root
      data-slot='checkbox'
      ref={ref}
      className={cn(checkboxVariants({ interaction, variant }), className)}
      {...props}>
      <CheckboxPrimitive.Indicator
        data-slot='checkbox-indicator'
        className='grid place-content-center text-current transition-none'>
        <CheckIcon className='size-3.5' />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )
})
Checkbox.displayName = 'Checkbox'

export { Checkbox }
